// The windows as the page's own DOM, for Snap and Look (Josh 10-01: phones were slow — every window was HTML painted
// into a canvas and uploaded to WebGL on every change). The same window modules, their DOM shown as it is: crisp
// text, native scrolling, the phone's own keyboard in the window's own field. No WebGL here, and three.js is never
// loaded for these two modes (an immersive session switches to core/render-gl.js).
//
// Snap: one window per screen, in the pinned row (core/snap-layout.js), each laid out at the screen's own width and
//   never scaled below 1 (Josh 10-02: "interfaces on non xr are very cramped") — a session window and Home at the
//   screen's shape, every other window as wide as the screen on a phone (no wider than its own size on a tablet) and
//   no taller than the band; a window that wants to know reads `ctx.screen` (a stream's chrome fits the Mac's picture
//   to that width). The phone's look — bigger targets, menus as sheets — is core/screen.css. A sideways swipe, the ‹ › buttons, the dots or the arrow keys move along the row: a CSS translate
//   on the compositor. Up and down is the window's own scrolling.
// Look: the same windows placed on the arc with CSS 3D transforms (a perspective stage; each window rotated and pushed
//   back to its place), turned by the phone's orientation, or by dragging on a desktop. On a phone a dot stays at the
//   screen's centre and a tap clicks what it is on; a double tap brings that window up to fill the screen (‹ Back, or
//   pinching in, puts it back), and then taps act on it directly (core/look-dot.js; docs/flows/look-mode.yaml).
//   Entering Look never brings a window up. On a phone the window brought up is laid flat over the arc at the
//   screen's width, as Snap shows it, so it reads at its own size.
//
// Touch, hover, press and the streams' pointers are core/dom-input.js; Look's dot is core/look-dot.js. Tiny Engineer
// stands beside a session window in Snap as its badge (tiny-engineer/robot-badges.js); the robots themselves are in
// the immersive room.
//
// One seam: `new DomWorld(app)` → `add(spec)`, `remove(spec)`, `resized(spec)`, `layout()`, `sync()`, `go(spec)`,
// `step(n)`, `current`, `mode`, `setMode(mode)`, `drawStrip()`, `screen(spec)`, `canvasChanged()`, `drawPointer(spec)`,
// `inView(spec)`, `embed(spec)`, `unembed(spec)`, `suspend()` / `resume()` (around an immersive session), `restyled()`.
// `app` is the app's half (app.js `domApp`).
import { DomInput } from './dom-input.js'
import { LookDot } from './look-dot.js'
import { DomEmbeds } from './dom-embeds.js'
import { RobotBadges } from '../tiny-engineer/robot-badges.js'
import { SCREEN } from './tokens.js'
import { screenA11y } from './screen-a11y.js'
import { screenBar } from './screen-bar.js'
import { screenMenus } from './screen-menus.js'
import { screenClose } from './screen-close.js'
import { ScreenSheets } from './screen-sheets.js'

const MIN_W = SCREEN.minLayoutWidth   // the narrowest a window is laid out at (points); a narrower screen shows it smaller
const K = 1000           // Look: CSS pixels to the metre
const FOV = 68           // Look: the vertical field of view, as the room's camera
const ZOOM_FILL = 0.92   // a window brought up fills this much of the screen
const BACK_ROW = SCREEN.minTarget + 8   // Look on a phone: the row ‹ Back stands in, above the window brought up

const div = (cls) => { const d = document.createElement('div'); d.className = cls; return d }
const reflows = (spec) => !!spec.dom && (spec.winKind === 'session' || spec.id === 'home')
/// A window's own DOM: a stream's is its chrome (windows/macwindow/macwindow.js), with the picture inside.
const domOf = (spec) => (spec.stream ? spec.chromeSpec?.dom : spec.dom) || null
/// A window laid out at the screen's width: every DOM window but the two above (a stream's chrome fits its picture
/// to the width it is given).
const fitsScreen = (spec) => !!domOf(spec) && !reflows(spec)
/// The size a window asked for (points), whatever the screen made of it.
const askedSize = (rec) => domOf(rec.spec)?.size || { w: parseFloat(rec.natural.w) || 620, h: parseFloat(rec.natural.h) || 760 }

export class DomWorld {
  constructor(app) {
    this.kind = 'dom'
    this.app = app
    this.mode = 'snap'
    this.current = null
    this.recs = new Map()          // spec → { spec, slot, frame, el, natural, scale }
    this.yaw = 0; this.pitch = 0   // Look: where the view points (degrees; yaw left is positive, as the arc's angles)
    this.zoomed = null             // Look: the window brought up to fill the screen
    this.host = document.getElementById('stage')
    this.stage = div('dw-stage snap')
    this.row = div('dw-row')
    this.cam = div('dw-cam')
    this.stage.append(this.row, this.cam)
    this.host.appendChild(this.stage)
    this.sheets = new ScreenSheets(this)   // a phone: a window placed 'front' is a sheet over the one in front
    this.back = document.createElement('button')
    this.back.className = 'dw-back glass'
    this.back.textContent = '‹ Back'
    this.back.hidden = true
    this.back.addEventListener('click', () => this.unzoom())
    document.body.appendChild(this.back)
    this.strip = {
      root: document.getElementById('strip'), name: document.getElementById('strip-name'),
      dots: document.getElementById('strip-dots'), prev: document.getElementById('strip-prev'), next: document.getElementById('strip-next'),
    }
    this.strip.prev?.addEventListener('click', () => this.step(-1))
    this.strip.next?.addEventListener('click', () => this.step(1))
    for (const id of ['reticle', 'select']) { const el = document.getElementById(id); if (el) el.hidden = true }
    this.input = new DomInput(this)
    this.dot = new LookDot(this)   // Look on a phone: the dot and its taps (the reticle above, shown only then)
    screenA11y(this.stage)
    screenBar()
    this.closer = screenClose(this)   // × and ⋯ › Close for the window in front
    for (const a of document.querySelectorAll('a.pill[data-mode]')) a.setAttribute('aria-current', String(a.dataset.mode === this.mode))
    screenMenus(this)
    this.badges = new RobotBadges({ store: app.robotsStore, settings: app.SETTINGS, macOf: app.macOf, clientOf: app.clientOf, component: app.COMPONENT.engineer, palette: app.COMPONENT.sessionColors?.palette })
    this.badgeTimer = setInterval(() => { if (!this.suspended) this.badges.update(this.app.sessionWindows, this.mode === 'snap' ? this.current : this.zoomed) }, 250)
    this.embeds = new DomEmbeds(app)
    addEventListener('resize', () => this.layout())
    // the plates change height on their own (a line arrives, the sheet opens): the band follows them
    if (typeof ResizeObserver !== 'undefined') {
      let timer = null
      const watch = new ResizeObserver(() => { clearTimeout(timer); timer = setTimeout(() => this.layout(), 60) })
      for (const id of ['top', 'sheet', 'live']) { const el = document.getElementById(id); if (el) watch.observe(el) }
    }
  }

  // MARK: the windows

  /// What shows for a window: its DOM; a stream's chrome with its picture inside; a canvas window's canvas.
  elementOf(spec) {
    if (spec.stream && spec.chromeSpec) return spec.chromeSpec.dom.root
    if (spec.dom) return spec.dom.root
    if (spec.canvas && spec.draw) {
      spec.canvas.style.width = (spec.logical || spec.canvas.width) + 'px'
      spec.canvas.style.height = Math.round(spec.canvas.height / (spec.canvas.width / (spec.logical || spec.canvas.width))) + 'px'
      spec.canvas.classList.add('dw-canvas')
      return spec.canvas
    }
    return null
  }

  add(spec) {
    if (spec.embed) { this.embed(spec); return }
    if (this.recs.has(spec) || spec.kind === 'orb') return
    const el = this.elementOf(spec)
    if (!el) return
    const slot = div('dw-slot'), frame = div('dw-frame')
    frame.appendChild(el)
    slot.appendChild(frame)
    const rec = { spec, slot, frame, el, natural: { w: el.style.width, h: el.style.height }, scale: 1 }
    this.recs.set(spec, rec)
    if (spec.stream) {
      // the picture shows inside the chrome (macwindow.js puts `stream.picture` in its slot)
      spec.player && (spec.player.picture = spec.canvas)
      try { spec.chromeSpec.dom.render() } catch (e) { console.error(e) }
      this.input.stream(rec)
    }
    ;(this.mode === 'look' ? this.cam : this.row).appendChild(slot)
    this.layout()
  }

  remove(spec) {
    if (spec.embed) { this.unembed(spec); return }
    const rec = this.recs.get(spec)
    if (!rec) return
    this.recs.delete(spec)
    if (spec.player) spec.player.picture = null
    rec.input?.dispose?.()
    this.sheets.drop(rec)
    rec.slot.remove()
    this.badges.drop(spec)
    if (this.current === spec) this.current = null
    if (this.zoomed === spec) this.unzoom()
  }

  /// A window asked for another size (the app set its points): fitted again.
  resized(spec) { const rec = this.recs.get(spec); if (rec && !rec.fitting) this.fit(rec) }
  restyled() { this.layout() }
  canvasChanged() {}

  /// The windows in the row, left to right: the open ones, by their place on the arc.
  order() {
    return [...this.recs.keys()].filter((s) => this.app.isLive(s.id) && s.snap !== false && !this.sheets?.wants(s)).sort((a, b) => (b.angle - a.angle) || (b.lift - a.lift))
  }

  // MARK: Snap's screen, and fitting a window to it

  /// The band the plates leave free: under the top bar, above the live plate.
  band() {
    const H = Math.max(1, innerHeight)
    let top = 0, bottom = 0
    for (const id of ['top']) { const el = document.getElementById(id); if (el && !el.hidden) top = Math.max(top, el.getBoundingClientRect().bottom) }
    const live = document.getElementById('live')
    if (live && !live.hidden && getComputedStyle(live).display !== 'none') bottom = H - live.getBoundingClientRect().top
    // never under the notch or the home indicator
    const safe = this.safeArea()
    top = Math.max(top, safe.top); bottom = Math.max(bottom, safe.bottom)
    // a phone: the window meets the bars (it is the page, core/screen-phone.css); wider, a little air around it
    const gap = innerWidth < SCREEN.phoneBelow ? 0 : 6
    return { top: Math.round(top + gap), bottom: Math.round(Math.max(0, bottom) + gap), w: innerWidth, h: Math.max(200, H - top - bottom - gap * 2) }
  }
  /// The screen's safe-area insets (the notch, the home indicator), as CSS gives them.
  safeArea() {
    if (!this.safeProbe) {
      this.safeProbe = div('dw-safe')
      this.safeProbe.style.cssText = 'position:fixed;left:0;top:0;visibility:hidden;pointer-events:none;padding:env(safe-area-inset-top,0px) 0 env(safe-area-inset-bottom,0px) 0'
      document.body.appendChild(this.safeProbe)
    }
    const s = getComputedStyle(this.safeProbe)
    return { top: parseFloat(s.paddingTop) || 0, bottom: parseFloat(s.paddingBottom) || 0 }
  }
  /// The size a reflowing window is laid out at in Snap (points), and the scale it is shown at.
  screenSize() {
    const b = this.band(), phone = innerWidth < SCREEN.phoneBelow, pad = phone ? 0 : 16   // a phone: edge to edge
    const w = b.w - pad * 2, h = b.h
    const s = w < MIN_W ? w / MIN_W : 1
    return { w: Math.round(w / s), h: Math.round(h / s), s, pad, phone }
  }
  /// What a window is told about the screen (ctx.screen): its size in Snap (or brought up flat in Look), or null (it
  /// keeps the headset's size).
  screen(spec) { return !this.suspended && (this.mode === 'snap' || this.zoomed === spec && this.flatZoom || this.sheets.wants(spec)) && (reflows(spec) || fitsScreen(spec)) ? this.screenSize() : null }

  fit(rec) {
    const { spec, el, frame } = rec
    rec.fitting = true
    try {
      if (rec.sheet) { this.sheets.fit(rec, this.screenSize(), askedSize(rec)); return }
      if (this.mode === 'snap' || rec.flat) {
        const S = this.screenSize()
        if (rec.flat) S.h -= BACK_ROW   // brought up in Look: ‹ Back has the row above it
        if (reflows(spec)) {
          if (spec.id === 'home') { el.style.width = S.w + 'px'; el.style.height = S.h + 'px' }
          else spec.dom.fit?.()
          rec.scale = S.s
        } else if (fitsScreen(spec)) {
          // as wide as the screen on a phone, no wider than it asked for on a tablet; no taller than the band
          const n = askedSize(rec)
          el.style.width = (S.phone ? S.w : Math.min(n.w, S.w)) + 'px'
          el.style.height = Math.min(n.h, S.h) + 'px'
          el.classList.toggle('on-screen', true)
          domOf(spec).screenChanged?.(S)
          rec.scale = S.s
        } else {
          const w = el.offsetWidth || parseFloat(el.style.width) || 600, h = el.offsetHeight || parseFloat(el.style.height) || 600
          rec.scale = Math.min(1.2, (this.band().w - S.pad * 2) / w, this.band().h / h)
        }
      } else {
        // Look: every window at the headset's size (points), placed and scaled by the 3D
        if (spec.id === 'home' && rec.natural.w) { el.style.width = rec.natural.w; el.style.height = rec.natural.h }
        else if (reflows(spec)) spec.dom.fit?.()
        else if (fitsScreen(spec)) { const n = askedSize(rec); el.style.width = n.w + 'px'; el.style.height = n.h + 'px'; el.classList.remove('on-screen'); domOf(spec).screenChanged?.(null) }
        rec.scale = 1
      }
    } finally { rec.fitting = false }
    // a frame at scale 1 has no transform, so a sheet inside it can stand against the screen
    frame.style.transform = (this.mode === 'snap' || rec.flat) && rec.scale !== 1 ? `scale(${rec.scale})` : ''
  }

  /// Every window to its place: the row's slots (Snap) or the arc (Look).
  layout() {
    if (this.suspended) return
    const b = this.band()
    this.stage.style.top = (this.mode === 'snap' ? b.top : 0) + 'px'
    this.stage.style.bottom = (this.mode === 'snap' ? b.bottom : 0) + 'px'
    for (const rec of this.recs.values()) { rec.slot.hidden = !this.app.isLive(rec.spec.id); this.sheets.place(rec) }
    if (this.mode === 'snap') this.layoutSnap(); else this.layoutLook()
    for (const rec of this.recs.values()) if (rec.sheet) this.fit(rec)
    this.drawStrip()
  }
  layoutSnap() {
    const list = this.order(), W = this.stage.clientWidth || innerWidth
    list.forEach((spec, i) => {
      const rec = this.recs.get(spec)
      this.fit(rec)
      rec.slot.style.transform = `translateX(${i * W}px)`
      rec.slot.style.width = W + 'px'
      rec.index = i
    })
    if (!list.includes(this.current)) this.current = list[0] || null
    this.slide(0, true)
  }
  /// The row moved so the current window is in front, plus `dx` pixels of a finger's drag (no animation while dragging).
  slide(dx = 0, animate = true) {
    const W = this.stage.clientWidth || innerWidth
    const i = this.order().indexOf(this.current)
    this.row.style.transition = animate ? 'transform 280ms cubic-bezier(.2,.8,.2,1)' : 'none'
    this.row.style.transform = `translateX(${-Math.max(0, i) * W + dx}px)`
    // only the window in front and its neighbours are drawn (the rest wait off screen, unpainted)
    for (const rec of this.recs.values()) rec.slot.classList.toggle('far', Math.abs((rec.index ?? 0) - i) > 1)
  }

  // MARK: Look — the arc in CSS 3D

  perspective() { return (innerHeight / 2) / Math.tan((FOV / 2) * Math.PI / 180) }
  layoutLook() {
    const P = this.perspective(), R = this.app.RADIUS
    this.stage.style.perspective = P + 'px'
    for (const rec of this.recs.values()) {
      const s = rec.spec
      if (rec.sheet) continue
      this.fit(rec)
      const r = s.radius ?? R, k = (r * K) / this.app.PPM   // CSS px per point at that distance
      rec.slot.style.width = ''
      rec.slot.classList.remove('far')
      rec.slot.style.transform = `rotateY(${s.angle || 0}deg) translateY(${-(s.lift || 0) * K}px) translateZ(${-r * K}px) scale(${k})`
    }
    this.aim()
  }
  /// The view: the camera's turn (and, brought up, its step towards the window).
  aim(animate = false) {
    const P = this.perspective()
    let yaw = this.yaw, pitch = this.pitch, dz = 0
    const z = this.zoomed && this.recs.get(this.zoomed)
    if (z) {
      const s = this.zoomed, r = (s.radius ?? this.app.RADIUS) * K, ppm = (r) / this.app.PPM
      const w = (z.el.offsetWidth || 600) * ppm, h = (z.el.offsetHeight || 600) * ppm
      const depth = Math.max(w * P / (innerWidth * ZOOM_FILL), h * P / (innerHeight * ZOOM_FILL))
      yaw = s.angle || 0; pitch = Math.atan2(s.lift || 0, s.radius ?? this.app.RADIUS) * 180 / Math.PI
      dz = r - depth
    }
    this.cam.style.transition = animate ? 'transform 320ms cubic-bezier(.2,.8,.2,1)' : 'none'
    this.cam.style.transform = `translateZ(${P + dz}px) rotateX(${pitch}deg) rotateY(${-yaw}deg)`
    if (!this.dotQueued && this.dot) this.dotQueued = requestAnimationFrame(() => { this.dotQueued = null; this.dot.probe() })   // what the dot is on now
  }
  /// Look: a window brought up to fill the screen (a double tap with the dot on it). Only in Look — Snap has nothing
  /// to bring up (Josh 10-17: a Look tap left half-done once brought Home up over Snap's row, and nothing under it
  /// could be tapped; going back to Look then laid that window over everything).
  zoomTo(spec) {
    if (this.mode !== 'look' || !this.recs.has(spec)) return
    if (this.zoomed && this.zoomed !== spec) this.unflat()
    this.zoomed = spec
    this.current = spec
    this.back.hidden = false
    // on a phone the window comes off the arc and lies flat at the screen's width, at its own size (Snap's fit)
    const rec = this.recs.get(spec)
    this.flatZoom = innerWidth < SCREEN.phoneBelow && !!rec
    if (this.flatZoom) {
      rec.flat = true
      if (!this.flat) { this.flat = div('dw-flat'); this.stage.appendChild(this.flat) }
      const b = this.band()
      this.flat.style.top = b.top + BACK_ROW + 'px'; this.flat.style.bottom = b.bottom + 'px'
      this.back.style.top = b.top + 'px'
      this.flat.appendChild(rec.frame)
      this.flat.hidden = false
      this.fit(rec)
    } else this.aim(true)
    this.dot?.sync()
    this.app.onChoose(spec)
    this.front(spec)
  }
  /// The window brought up flat goes back to its place on the arc.
  unflat() {
    const rec = this.zoomed && this.recs.get(this.zoomed)
    if (!rec?.flat) return
    rec.flat = false
    rec.slot.appendChild(rec.frame)
    if (this.flat) this.flat.hidden = true
    this.back.style.top = ''
    this.flatZoom = false
    this.fit(rec)
  }
  unzoom() {
    if (!this.zoomed) return
    this.unflat()
    this.zoomed = null
    this.back.hidden = true
    this.aim(true)
    this.dot?.sync()
    this.closer?.update()
  }

  // MARK: moving along

  go(spec) {
    if (!spec) return
    const sheet = this.recs.get(spec)?.sheet ? this.recs.get(spec) : null
    if (sheet) { this.sheets.raise(sheet); this.front(spec); return }   // a sheet: in front of the others, the row stays
    if (this.mode === 'look') {
      if (!this.recs.has(spec)) return
      this.current = spec
      if (this.zoomed) this.zoomTo(spec)
      else { this.yaw = spec.angle || 0; this.pitch = 0; this.input.recalibrate?.(); this.aim(true) }
      this.front(spec)
      this.app.onChoose(spec)
      return
    }
    if (!this.recs.has(spec)) return
    const fronted = spec !== this.current
    this.current = spec
    this.slide(0, true)
    this.drawStrip()
    if (fronted) this.front(spec)
    this.app.onChoose(spec)
  }
  /// Who came to the front, for whoever cares (an extension session's Agent pane sends its `focus` action).
  front(spec) { this.closer?.update(); try { dispatchEvent(new CustomEvent('cody:front', { detail: { id: spec.id, kind: spec.winKind || spec.kind || null, sessionID: spec.sessionID || null } })) } catch {} }
  step(by) {
    const list = this.order()
    if (!list.length) return
    let i = list.indexOf(this.current)
    if (i < 0) i = 0
    const next = i + by
    if (next < 0 || next >= list.length) { this.slide(0, true); return }   // the row's ends: it springs back
    this.go(list[next])
  }
  stepVertical() {}

  drawStrip() {
    if (!this.strip.root) return
    const list = this.order()
    this.strip.root.hidden = this.mode !== 'snap' || !list.length
    if (this.current && !list.includes(this.current)) this.current = list[0] || null
    this.strip.name.textContent = this.current ? (this.current.short || this.current.name) : ''
    this.closer?.update()
    this.strip.dots.replaceChildren(...list.map((s) => { const d = document.createElement('i'); if (s === this.current) d.className = 'on'; d.title = s.short || s.name; d.addEventListener('click', () => this.go(s)); return d }))
  }

  /// Snap or Look. Look asks for the phone's orientation (iOS asks once, from the tap) and waits up to a second for
  /// it; the latest tap wins — Snap tapped in that second stays Snap, not Look once the wait ends.
  async setMode(mode) {
    if (mode !== 'snap' && mode !== 'look') return
    const ask = this.modeAsk = (this.modeAsk || 0) + 1
    this.modeWanted = mode
    if (mode === 'look' && this.mode !== 'look') {
      await this.input.lookOn()
      if (ask !== this.modeAsk) { if (this.modeWanted !== 'look') this.input.lookOff(); return }
      this.yaw = this.current?.angle || 0; this.pitch = 0
    }
    // either way nothing is brought up: entering Look shows the arc (a double tap brings a window up), and Snap has none
    this.unzoom()
    if (mode === 'snap') { this.input.lookOff(); this.dot?.off() }
    this.mode = mode
    this.stage.classList.toggle('snap', mode === 'snap')
    this.stage.classList.toggle('look', mode === 'look')
    const holder = mode === 'look' ? this.cam : this.row
    for (const rec of this.recs.values()) { holder.appendChild(rec.slot); rec.slot.style.transform = '' }
    this.row.style.transform = ''
    document.body.dataset.view = mode
    for (const a of document.querySelectorAll('a.pill[data-mode]')) a.setAttribute('aria-current', String(a.dataset.mode === mode))
    try { localStorage.setItem('cody.view', mode) } catch {}
    this.app.onMode(mode)
    this.layout()
    if (this.current) this.go(this.current)
    if (mode === 'look') this.dot?.on()
  }

  // MARK: streams

  /// Whether a stream is being looked at: the window in front (or the session window it sits in); in Look, near the view.
  inView(spec) {
    const s = spec.embed || spec
    if (this.mode === 'snap') return this.current === s
    if (this.zoomed) return this.zoomed === s
    return Math.abs(((s.angle || 0) - this.yaw + 540) % 360 - 180) < 60
  }
  drawPointer(spec) { this.input.drawPointer(spec) }

  /// A stream inside a session window's pane: its canvas in the pane's slot (dom-embeds.js).
  embed(spec) { this.input.embed(spec); this.embeds.add(spec) }
  unembed(spec) { this.input.unembed(spec); this.embeds.remove(spec) }
  placeEmbeds() { this.embeds.place() }

  // MARK: around an immersive session (render-gl.js takes the windows, then gives them back)

  suspend() {
    this.suspended = true
    this.stage.hidden = true
    this.back.hidden = true
    this.dot?.sync()
    this.badges.hide()
    for (const rec of this.recs.values()) if (rec.spec.player) rec.spec.player.picture = null
  }
  resume() {
    this.suspended = false
    this.stage.hidden = false
    for (const rec of this.recs.values()) {
      rec.frame.appendChild(rec.el)
      if (rec.spec.player) { rec.spec.player.picture = rec.spec.canvas; try { rec.spec.chromeSpec?.dom.render() } catch {} }
    }
    this.placeEmbeds()
    this.layout()
    if (this.current) this.go(this.current)
    this.dot?.sync()
  }
  sync() { this.layout() }
}
